import Seo from "@/components/Seo";
import PageHero from "@/components/PageHero";
import PtfButton from "@/components/PtfButton";
import { Reveal, MaskText } from "@/components/motion/Reveal";
import { PILLARS, IMAGES } from "@/lib/content";
import { Link } from "react-router-dom";
import { ArrowUpRight } from "lucide-react";
import { useTranslation, Trans } from "react-i18next";

const Eyebrow = ({ children }) => (
  <p className="text-[0.7rem] uppercase tracking-[0.28em] text-ptf-primary/60 mb-5 flex items-center gap-3">
    <span className="h-px w-8 bg-ptf-primary/40 inline-block" /> {children}
  </p>
);

function WhoWeAre() {
  const { t } = useTranslation();
  return (
    <section className="bg-ptf-bg py-24 md:py-32">
      <div className="mx-auto max-w-[1600px] px-5 sm:px-8 lg:px-12 grid grid-cols-1 lg:grid-cols-12 gap-12">
        <div className="lg:col-span-5">
          <Reveal><Eyebrow>{t("about.who_eyebrow")}</Eyebrow></Reveal>
          <MaskText lines={t("about.who_headline", { returnObjects: true })} className="font-display font-extrabold uppercase text-4xl lg:text-6xl leading-[0.92] tracking-tight text-ptf-primary" />
        </div>
        <div className="lg:col-span-7">
          <Reveal delay={0.1}>
            <p className="text-lg leading-relaxed text-ptf-text/80">
              {t("about.who_p1")}
            </p>
            <p className="mt-6 leading-relaxed text-ptf-text/70">
              <Trans
                i18nKey="about.who_p2"
                components={{
                  link1: <Link to="/focus" className="underline underline-offset-4 hover:text-ptf-primary" />,
                  link2: <Link to="/programmes" className="underline underline-offset-4 hover:text-ptf-primary" />,
                }}
              >
                Our objects are deliberately broad. They span conservation, responsible
                tourism, community development, education, livelihoods, agriculture,
                health, infrastructure, youth, sports, research and sustainable
                technology. What unifies them is practical: in India, conservation holds
                only where the communities nearby benefit from it. You can read each area
                in detail on <link1>Our Focus</link1>,
                and follow how they become real work on the{" "}
                <link2>Programmes</link2> page.
              </Trans>
            </p>
          </Reveal>
        </div>
      </div>
    </section>
  );
}

function VisionMission() {
  const { t } = useTranslation();
  return (
    <section className="bg-ptf-primary text-ptf-bg py-24 md:py-32">
      <div className="mx-auto max-w-[1600px] px-5 sm:px-8 lg:px-12 grid grid-cols-1 md:grid-cols-2 gap-px bg-ptf-bg/10 border border-ptf-bg/10">
        <Reveal className="h-full">
          <div className="bg-ptf-primary p-8 md:p-12 h-full">
            <p className="text-[0.65rem] uppercase tracking-[0.24em] text-ptf-accent mb-6">{t("about.vision_label")}</p>
            <h2 className="font-display uppercase text-3xl md:text-4xl leading-tight tracking-tight">
              {t("about.vision_title")}
            </h2>
            <p className="mt-6 text-ptf-bg/70 leading-relaxed">
              {t("about.vision_p")}
            </p>
          </div>
        </Reveal>
        <Reveal delay={0.1} className="h-full">
          <div className="bg-ptf-primary p-8 md:p-12 h-full">
            <p className="text-[0.65rem] uppercase tracking-[0.24em] text-ptf-accent mb-6">{t("about.mission_label")}</p>
            <h2 className="font-display uppercase text-3xl md:text-4xl leading-tight tracking-tight">
              {t("about.mission_title")}
            </h2>
            <p className="mt-6 text-ptf-bg/70 leading-relaxed">
              {t("about.mission_p")}
            </p>
          </div>
        </Reveal>
      </div>
    </section>
  );
}

function Beliefs() {
  const { t } = useTranslation();
  const principles = t("about.principles", { returnObjects: true });
  return (
    <section className="bg-ptf-bg py-24 md:py-32">
      <div className="mx-auto max-w-[1600px] px-5 sm:px-8 lg:px-12">
        <Reveal><Eyebrow>{t("about.beliefs_eyebrow")}</Eyebrow></Reveal>
        <div className="border-t border-ptf-primary/15">
          {principles.map((b, i) => (
            <Reveal key={b} delay={i * 0.05}>
              <div className="group flex items-baseline gap-6 py-7 border-b border-ptf-primary/15">
                <span className="font-display text-xl text-ptf-primary/40 shrink-0">{String(i + 1).padStart(2, "0")}</span>
                <p className="font-display uppercase text-2xl md:text-4xl leading-tight tracking-tight text-ptf-primary group-hover:text-ptf-secondary transition-colors">{b}</p>
              </div>
            </Reveal>
          ))}
        </div>
      </div>
    </section>
  );
}

function AreasList() {
  const { t } = useTranslation();
  return (
    <section className="bg-ptf-secondary text-ptf-bg py-24 md:py-32">
      <div className="mx-auto max-w-[1600px] px-5 sm:px-8 lg:px-12">
        <div className="mb-12 max-w-2xl">
          <MaskText lines={t("about.areas_headline", { returnObjects: true })} className="font-display font-extrabold uppercase text-4xl lg:text-6xl leading-[0.92] tracking-tight" />
        </div>
        <div className="grid grid-cols-1 sm:grid-cols-2 gap-x-10 gap-y-1">
          {PILLARS.map((p) => (
            <Link key={p.slug} to={`/focus#${p.slug}`} className="group flex items-center justify-between gap-4 py-5 border-b border-ptf-bg/15 hover:border-ptf-accent transition-colors">
              <span className="flex items-baseline gap-4">
                <span className="font-display text-lg text-ptf-accent">{p.num}</span>
                <span className="font-display uppercase text-xl md:text-2xl tracking-tight">{p.title}</span>
              </span>
              <ArrowUpRight className="h-5 w-5 text-ptf-bg/40 group-hover:text-ptf-accent transition-colors" />
            </Link>
          ))}
        </div>
      </div>
    </section>
  );
}

function Governance() {
  const { t } = useTranslation();
  return (
    <section className="bg-ptf-bg py-24 md:py-32">
      <div className="mx-auto max-w-[1600px] px-5 sm:px-8 lg:px-12 grid grid-cols-1 lg:grid-cols-2 gap-12 lg:gap-20">
        <Reveal>
          <Eyebrow>{t("about.gov_section8_eyebrow")}</Eyebrow>
          <h3 className="font-display uppercase text-3xl md:text-4xl tracking-tight text-ptf-primary leading-tight">{t("about.gov_section8_title")}</h3>
          <p className="mt-6 leading-relaxed text-ptf-text/75">
            {t("about.gov_section8_p1")}
          </p>
          <p className="mt-4 leading-relaxed text-ptf-text/70">
            <Trans i18nKey="about.gov_section8_p2" components={{ link1: <Link to="/transparency" className="underline underline-offset-4 hover:text-ptf-primary" /> }}>
              Our formal registration documents will be published on our{" "}
              <link1>Transparency</link1> page as they become available.
            </Trans>
          </p>
          <div className="mt-8 border border-ptf-primary/15 bg-white/60 p-6" data-testid="registration-details">
            <p className="text-[0.65rem] uppercase tracking-[0.18em] text-ptf-secondary mb-4">{t("about.reg_details_title")}</p>
            <dl className="space-y-3 text-sm">
              {["reg_incorporated", "reg_state", "reg_cin", "reg_pan", "reg_tan"].map((k) => (
                <div key={k} className="flex flex-wrap justify-between gap-2 border-t border-ptf-primary/10 pt-3 first:border-t-0 first:pt-0">
                  <dt className="text-ptf-text/55">{t(`about.${k}_label`)}</dt>
                  <dd className="font-medium text-ptf-primary" data-testid={`${k}-value`}>{t(`about.${k}_value`)}</dd>
                </div>
              ))}
            </dl>
          </div>
          <div className="mt-8" data-testid="foundation-objectives">
            <p className="text-[0.65rem] uppercase tracking-[0.18em] text-ptf-secondary mb-4">{t("about.objectives_title")}</p>
            <ul className="space-y-3 text-sm text-ptf-text/75 leading-relaxed">
              {t("about.objectives_list", { returnObjects: true }).map((o) => (
                <li key={o} className="pl-4 border-l-2 border-ptf-secondary/40">{o}</li>
              ))}
            </ul>
          </div>
        </Reveal>
        <Reveal delay={0.1}>
          <Eyebrow>{t("about.gov_governance_eyebrow")}</Eyebrow>
          <h3 className="font-display uppercase text-3xl md:text-4xl tracking-tight text-ptf-primary leading-tight">{t("about.gov_governance_title")}</h3>
          <p className="mt-6 leading-relaxed text-ptf-text/75">
            {t("about.gov_governance_p")}
          </p>
          <div className="mt-8">
            <PtfButton to="/partnerships">{t("about.gov_governance_cta")}</PtfButton>
          </div>
        </Reveal>
      </div>
    </section>
  );
}

export default function About() {
  const { t } = useTranslation();
  const faqs = t("about.faqs", { returnObjects: true });
  const faqSchema = {
    "@context": "https://schema.org",
    "@type": "FAQPage",
    mainEntity: faqs.map((f) => ({
      "@type": "Question",
      name: f.q,
      acceptedAnswer: { "@type": "Answer", text: f.a },
    })),
  };
  return (
    <>
      <Seo
        title="About"
        path="/about"
        description="About Planet Trotter Foundation, an Indian Section 8 organisation based in India and working globally toward environmental and social impact."
        jsonLd={faqSchema}
      />
      <PageHero
        eyebrow={t("about.hero_eyebrow")}
        titleLines={t("about.hero_title", { returnObjects: true })}
        intro={t("about.hero_intro")}
        image={IMAGES.aboutHero}
      />
      <WhoWeAre />
      <VisionMission />
      <Beliefs />
      <AreasList />
      <Governance />
      <section className="bg-ptf-bg pb-24 md:pb-32">
        <div className="mx-auto max-w-[1600px] px-5 sm:px-8 lg:px-12">
          <Eyebrow>{t("about.faq_eyebrow")}</Eyebrow>
          <div className="mt-2 grid grid-cols-1 lg:grid-cols-2 gap-x-16 gap-y-10 max-w-5xl">
            {faqs.map((f) => (
              <div key={f.q} data-testid="about-faq-item">
                <h3 className="font-display uppercase text-xl md:text-2xl tracking-tight text-ptf-primary leading-tight">{f.q}</h3>
                <p className="mt-3 text-sm md:text-base text-ptf-text/75 leading-relaxed">{f.a}</p>
              </div>
            ))}
          </div>
        </div>
      </section>
    </>
  );
}
